/* ==========================================================================================
   Commercial Equipment Online 2026 · ce-pages-a.js · behaviour for the designed pages (pages-a, launch wave 10-10-2026)
   No dependencies; deferred; safe to load twice; every part binds once and only where its markup is on the page.
     [data-cepa-draw]      the CEO Promise shield draws itself once when it is seen (CSS does the drawing)
     [data-cepa-finder]    Warranty & Service brand finder: search + A-Z + the brand's claim panel
     [data-cepa-wall]      Our Brands wall: search + letter jump + "no brand found"
     [data-cepa-quote]     Request a quote: our list of the O: Request a Quote app's items (read from the app's own
                           saved list in this browser) + "Review and send" opens the app's own quote window
     [data-cepa-calc]      SilverChef weekly guide (price x 0.055 x 12 / 52, the live product page formula)
     [data-cepa-map]       About: the delivery dots from the home page's real-order map data
   Reduced motion: nothing animates (CSS); the scripts never move the page by themselves.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEPA) return;
  const W = window, D = document;
  const reduce = () => W.matchMedia && W.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const once = (el, key) => { if (el.dataset['cepaBound' + key]) return false; el.dataset['cepaBound' + key] = '1'; return true; };
  const esc = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

  /* ---------- the shield draws itself ---------- */
  function draw(root) {
    root.querySelectorAll('[data-cepa-draw]').forEach((svg) => {
      if (!once(svg, 'Draw') || reduce() || !('IntersectionObserver' in W)) return;
      svg.classList.add('is-armed');
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { svg.classList.add('is-on'); io.disconnect(); } });
      }, { threshold: 0.25 });
      io.observe(svg);
      setTimeout(() => svg.classList.add('is-on'), 4000);
    });
  }

  /* ---------- Warranty & Service: the brand finder ---------- */
  function finder(root) {
    root.querySelectorAll('[data-cepa-finder]').forEach((box) => {
      if (!once(box, 'Finder')) return;
      const t = (k) => box.getAttribute('data-t-' + k) || '';
      const email = box.getAttribute('data-email') || '';
      const subjectT = box.getAttribute('data-subject') || 'Warranty claim - [brand]';
      const bodyT = box.getAttribute('data-body') || '';
      const q = box.querySelector('[data-cepa-q]');
      const count = box.querySelector('[data-cepa-count]');
      const none = box.querySelector('[data-cepa-none]');
      const noneText = box.querySelector('[data-cepa-none-text]');
      const items = [...box.querySelectorAll('.cepa-brands__item')];
      const letters = [...box.querySelectorAll('[data-cepa-letter]')];
      const panel = box.querySelector('[data-cepa-panel]');
      const pTitle = box.querySelector('[data-cepa-panel-title]');
      const pText = box.querySelector('[data-cepa-panel-text]');
      const pActions = box.querySelector('[data-cepa-panel-actions]');
      const keep = { title: pTitle && pTitle.innerHTML, text: pText && pText.innerHTML, actions: pActions && pActions.innerHTML };
      const names = items.map((li) => {
        const a = li.querySelector('.cepa-brand');
        return norm(li.getAttribute('data-cepa-name') + ' ' + ((a && a.getAttribute('data-supplier')) || ''));
      });
      let letter = '';
      const mq = W.matchMedia('(max-width: 989px)');
      const phone = W.matchMedia('(max-width: 749px)');
      const moreWrap = box.querySelector('[data-cepa-more-wrap]');
      const moreBtn = box.querySelector('[data-cepa-more]');
      const LIMIT = 18;
      let expanded = false;

      const mailto = (brand) => 'mailto:' + email + '?subject=' + encodeURIComponent(subjectT.replace('[brand]', brand)) + (bodyT ? '&body=' + encodeURIComponent(bodyT) : '');
      const icon = (name) => {
        const p = { mail: '<rect x="3" y="5.4" width="18" height="13.2" rx="2.2"/><path class="a" d="m4.2 7.4 7.8 5.7 7.8-5.7"/>', phone: '<path d="M6.7 3.6h2.5l1.3 4.1-1.8 1.4a11.4 11.4 0 0 0 6.3 6.3l1.4-1.8 4.1 1.3v2.5a2.2 2.2 0 0 1-2.4 2.2A16.4 16.4 0 0 1 4.5 6a2.2 2.2 0 0 1 2.2-2.4z"/>', ext: '<path d="M14 4.2h5.8V10M19.6 4.4l-8.8 8.8"/><path d="M18 14v4.6a1.4 1.4 0 0 1-1.4 1.4H5.4A1.4 1.4 0 0 1 4 18.6V7.4A1.4 1.4 0 0 1 5.4 6H10"/>' }[name];
        return '<svg class="cepa-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + p + '</svg>';
      };

      function filter() {
        const term = norm(q ? q.value : '');
        let n = 0;
        items.forEach((li, i) => {
          const ok = (!letter || li.getAttribute('data-cepa-l') === letter) && (!term || names[i].indexOf(term) > -1);
          li.hidden = !ok;
          if (ok) n++;
        });
        // phones: the first 18 brands, then "Show all" (search and A-Z always look through every brand)
        const fold = phone.matches && !expanded && !letter && !term && n > LIMIT;
        if (fold) { let k = 0; items.forEach((li) => { if (!li.hidden) { k++; if (k > LIMIT) li.hidden = true; } }); }
        if (moreWrap) moreWrap.hidden = !fold;
        if (count) count.textContent = n === 1 && t('count-one') ? t('count-one') : t('count').replace('[count]', n);
        if (none) {
          none.hidden = n > 0;
          if (noneText) noneText.textContent = t('none').replace('[q]', q ? q.value.trim() : '');
        }
      }
      letters.forEach((b) => b.addEventListener('click', () => {
        letter = b.getAttribute('data-cepa-letter') || '';
        letters.forEach((x) => { const on = x === b; x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        if (q && q.value) q.value = '';
        filter();
      }));
      if (q) {
        q.addEventListener('input', () => {
          if (letter) { letter = ''; letters.forEach((x) => { const on = !x.getAttribute('data-cepa-letter'); x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); }); }
          filter();
        });
        q.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const first = items.find((li) => !li.hidden);
            if (first) first.querySelector('.cepa-brand').click();
          }
        });
      }

      function closeSheet() {
        if (!panel) return;
        panel.classList.remove('is-sheet');
        D.documentElement.classList.remove('cepa-sheet-open');
      }
      function show(a) {
        const brand = a.getAttribute('data-cepa-brand') || '';
        const sup = a.getAttribute('data-supplier') || '';
        const url = a.getAttribute('data-url') || '';
        const phone = a.getAttribute('data-phone') || '';
        const mail = a.getAttribute('data-mail') || '';
        const notes = a.getAttribute('data-notes') || '';
        box.querySelectorAll('.cepa-brand.is-picked').forEach((x) => { x.classList.remove('is-picked'); x.removeAttribute('aria-current'); });
        a.classList.add('is-picked');
        a.setAttribute('aria-current', 'true');
        const logo = a.querySelector('.cepa-brand__logo img');
        if (logo && logo.loading === 'lazy') logo.loading = 'eager';
        pTitle.innerHTML = (logo ? '<span class="cepa-claim__logo"><img src="' + esc(logo.currentSrc || logo.src) + '" alt="" loading="eager"></span>' : '') + '<span>' + esc(brand) + '</span>';
        const supName = sup || brand;
        const known = !!(sup || url || phone || mail);
        pText.textContent = (known ? t('known') : t('unknown')).replace(/\[brand\]/g, brand).replace(/\[supplier\]/g, supName);
        let html = '';
        // Tommy 10-10: a supplier may have more than one form (NutriFaster: Blendtec drop-off, or collection) -
        // data-forms lists them; each gets its own button, the first the main one
        let forms = [];
        try { forms = JSON.parse(a.getAttribute('data-forms') || '[]'); } catch (e) { forms = []; }
        if (known) {
          if (forms.length > 1) {
            forms.forEach((f, k) => {
              if (!f || !f.url) return;
              html += '<a class="ce-btn' + (k ? ' ce-btn--secondary' : '') + ' cepa-claim__form" href="' + esc(f.url) + '" target="_blank" rel="noopener">' + icon('ext') + '<span>' + esc(f.label || t('claim').replace(/\[supplier\]/g, supName)) + '</span><span class="ce-vh"> ' + esc(t('newtab')) + '</span></a>';
            });
          } else if (url) html += '<a class="ce-btn" href="' + esc(url) + '" target="_blank" rel="noopener">' + icon('ext') + '<span>' + esc(t('claim').replace(/\[supplier\]/g, supName)) + '</span><span class="ce-vh"> ' + esc(t('newtab')) + '</span></a>';
          if (phone) html += '<a class="cepa-claim__line" href="tel:' + esc(phone.replace(/[^\d+]/g, '')) + '">' + icon('phone') + '<span>' + esc(t('call').replace(/\[supplier\]/g, supName)) + ': <b>' + esc(phone) + '</b></span></a>';
          if (mail) html += '<a class="cepa-claim__line" href="mailto:' + esc(mail) + '">' + icon('mail') + '<span>' + esc(t('email-sup').replace(/\[supplier\]/g, supName)) + ': <b>' + esc(mail) + '</b></span></a>';
          if (notes) html += '<p class="cepa-claim__notes">' + esc(notes) + '</p>';
          html += '<p class="cepa-claim__help">' + esc(t('help')) + ' <a href="' + esc(mailto(brand)) + '">' + esc(email) + '</a></p>';
        } else {
          html += '<a class="ce-btn" href="' + esc(mailto(brand)) + '">' + icon('mail') + '<span>' + esc(t('mail')) + '</span></a>';
        }
        pActions.innerHTML = html;
        if (mq.matches) {
          panel.classList.add('is-sheet');
          D.documentElement.classList.add('cepa-sheet-open');
        }
        panel.focus({ preventScroll: !mq.matches ? true : true });
      }
      box.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('.cepa-brand');
        if (a && box.contains(a)) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
          e.preventDefault();
          show(a);
          return;
        }
        if (e.target.closest && e.target.closest('[data-cepa-sheet-close]')) closeSheet();
        if (e.target === panel) closeSheet();
      });
      D.addEventListener('keydown', (e) => { if (e.key === 'Escape' && panel && panel.classList.contains('is-sheet')) { const picked = box.querySelector('.cepa-brand.is-picked'); closeSheet(); if (picked) picked.focus(); } });
      mq.addEventListener && mq.addEventListener('change', () => { if (!mq.matches) closeSheet(); });
      if (panel && !panel.querySelector('[data-cepa-sheet-close]')) {
        const x = D.createElement('button');
        x.type = 'button';
        x.className = 'cepa-claim__close';
        x.setAttribute('data-cepa-sheet-close', '');
        x.setAttribute('aria-label', 'Close');
        x.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';
        panel.appendChild(x);
      }
      if (moreBtn) moreBtn.addEventListener('click', () => { expanded = true; filter(); const nxt = items[LIMIT] && items[LIMIT].querySelector('.cepa-brand'); if (nxt) nxt.focus(); });
      phone.addEventListener && phone.addEventListener('change', filter);
      box.classList.add('is-live');
      filter();
      void keep;
    });
  }

  /* ---------- Contact: copy the address; "Open now" in Brisbane time (no daylight saving in Queensland) ---------- */
  function contact(root) {
    root.querySelectorAll('[data-cepa-copy]').forEach((btn) => {
      if (!once(btn, 'Copy') || !(navigator.clipboard && W.isSecureContext)) return;
      btn.hidden = false;
      const label = btn.querySelector('span');
      const idle = label ? label.textContent : '';
      const live = btn.closest('li') && btn.closest('li').querySelector('[data-cepa-copy-live]');
      let timer = 0;
      btn.addEventListener('click', () => {
        const addr = btn.getAttribute('data-cepa-copy');
        navigator.clipboard.writeText(addr).then(() => {
          const done = btn.getAttribute('data-done') || 'Copied';
          btn.classList.add('is-done');
          if (label) label.textContent = done;
          if (live) live.textContent = done + ': ' + addr;
          clearTimeout(timer);
          timer = setTimeout(() => { btn.classList.remove('is-done'); if (label) label.textContent = idle; }, 2400);
        }).catch(() => { W.location.href = 'mailto:' + addr; });
      });
    });
    root.querySelectorAll('[data-cepa-open]').forEach((el) => {
      if (!once(el, 'Open')) return;
      const span = (el.getAttribute('data-cepa-open') || '').split('-');
      const toMin = (t) => { const m = /^(\d{1,2}):?(\d{2})?$/.exec(String(t).trim()); return m ? (+m[1]) * 60 + (+(m[2] || 0)) : NaN; };
      const open = toMin(span[0]), close = toMin(span[1]);
      if (isNaN(open) || isNaN(close) || close <= open) return;
      let day, min;
      try {
        const bits = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
        const get = (t) => { const p = bits.find((x) => x.type === t); return p ? p.value : ''; };
        day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
        min = (+get('hour')) * 60 + (+get('minute'));
      } catch (e) { return; }
      if (day < 0) return;
      const isOpen = day >= 1 && day <= 5 && min >= open && min < close;
      el.textContent = isOpen ? el.getAttribute('data-open-text') : el.getAttribute('data-closed-text');
      el.classList.toggle('is-open', isOpen);
      el.hidden = false;
    });
  }

  /* ---------- Request a quote: the O: Request a Quote app's list, shown in our layout ----------
     The app keeps the list in this browser (localStorage "<shop>.myshopify.com_productIdAddQuoteList"). We only READ it;
     "Add your details and send" presses the app's own View Quote button, so the app's window does the sending. */
  function quote(root) {
    root.querySelectorAll('[data-cepa-quote]').forEach((box) => {
      if (!once(box, 'Quote')) return;
      const shop = (W.Shopify && W.Shopify.shop) || box.getAttribute('data-shop') || '';
      const key = shop + '_productIdAddQuoteList';
      const panel = box.querySelector('[data-cepa-quote-panel]');
      if (!panel) return;
      const t = (k) => panel.getAttribute('data-t-' + k) || '';
      const list = panel.querySelector('[data-cepa-quote-items]');
      const empty = panel.querySelector('[data-cepa-quote-empty]');
      const foot = panel.querySelector('[data-cepa-quote-foot]');
      const sum = panel.querySelector('[data-cepa-quote-sum]');
      const counts = box.querySelectorAll('[data-cepa-quote-count]');
      let last = null;
      const read = () => { try { return localStorage.getItem(key) || ''; } catch (e) { return ''; } };
      const appBtn = () => D.querySelector('.omgrfq_view_quote_btn') || D.querySelector('button.omgrfq_view_quote');
      const appCount = () => { const b = appBtn(); const m = b && /(\d+)/.exec(b.textContent || ''); return m ? +m[1] : null; };
      const img = (src) => { if (!src) return ''; const u = String(src); const sized = /\?/.test(u) ? u + '&width=160' : u + '?width=160'; return '<img src="' + esc(sized) + '" alt="" width="80" height="80" loading="lazy">'; };
      function render() {
        const raw = read();
        if (raw === last) return;
        last = raw;
        let items = [];
        try { const v = JSON.parse(raw || '[]'); if (Array.isArray(v)) items = v; } catch (e) { items = []; }
        const n = items.length || (raw ? 0 : (appCount() || 0));
        list.innerHTML = items.map((it) => {
          const vt = it && it.variant && it.variant.title && !/default title/i.test(it.variant.title) ? it.variant.title : '';
          const href = it && it.handle ? '/products/' + encodeURIComponent(it.handle) : '';
          return '<li class="cepa-qitem">' +
            '<span class="cepa-qitem__img">' + img(it && it.image) + '</span>' +
            '<span class="cepa-qitem__words">' + (href ? '<a href="' + esc(href) + '">' + esc(it.title || '') + '</a>' : esc(it && it.title)) +
            (it && it.vendor ? '<small>' + esc(it.vendor) + (vt ? ' · ' + esc(vt) : '') + '</small>' : '') + '</span>' +
            '<span class="cepa-qitem__qty">' + esc(t('qty')) + ' <b>' + esc(it && it.quantity != null ? it.quantity : 1) + '</b></span></li>';
        }).join('');
        const has = items.length > 0;
        list.hidden = !has;
        empty.hidden = has;
        foot.hidden = !has;
        if (sum) sum.textContent = has ? (items.length === 1 ? t('one') : t('items').replace('[count]', items.length)) : '';
        counts.forEach((c) => { c.textContent = n; c.hidden = !n; });
      }
      panel.addEventListener('click', (e) => {
        if (!(e.target.closest && e.target.closest('[data-cepa-quote-send]'))) return;
        const b = appBtn();
        if (b) b.click(); else W.location.hash = 'cepa-quote-list';
      });
      box.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('[data-cepa-quote-open]');
        if (!a) return;
        const has = (() => { try { return JSON.parse(read() || '[]').length > 0; } catch (x) { return false; } })();
        const b = appBtn();
        if (has && b) { e.preventDefault(); b.click(); }
      });
      render();
      W.addEventListener('storage', (e) => { if (e.key === key) render(); });
      setInterval(() => { if (!D.hidden) render(); }, 1200);
    });
  }

  /* ---------- Our Brands: search the wall (letters are plain links to each group) ---------- */
  function wall(root) {
    root.querySelectorAll('[data-cepa-wall]').forEach((box) => {
      if (!once(box, 'Wall')) return;
      const sec = box.querySelector('.cepa-wall');
      const q = box.querySelector('[data-cepa-wall-q]');
      const count = box.querySelector('[data-cepa-wall-count]');
      const none = box.querySelector('[data-cepa-wall-none]');
      const noneText = box.querySelector('[data-cepa-wall-none-text]');
      const searchBtn = box.querySelector('[data-cepa-wall-search]');
      const groups = [...box.querySelectorAll('[data-cepa-group]')];
      const tiles = [...box.querySelectorAll('.cepa-wtile-li')];
      const more = box.querySelector('[data-cepa-more]');
      const moreItems = more ? [...more.querySelectorAll('li')] : [];
      const names = tiles.map((li) => norm(li.getAttribute('data-cepa-name')));
      const moreNames = moreItems.map((li) => norm(li.getAttribute('data-cepa-name')));
      const t = (k) => (sec && sec.getAttribute('data-t-' + k)) || '';
      const jumps = [...box.querySelectorAll('[data-cepa-jump]')];
      if (!q) return;
      const run = () => {
        const term = norm(q.value);
        let n = 0;
        tiles.forEach((li, i) => { const ok = !term || names[i].indexOf(term) > -1; li.hidden = !ok; if (ok) n++; });
        groups.forEach((g) => { g.hidden = !g.querySelector('.cepa-wtile-li:not([hidden])'); });
        let m = 0;
        moreItems.forEach((li, i) => { const ok = !term || moreNames[i].indexOf(term) > -1; li.hidden = !ok; if (ok) m++; });
        if (more) more.hidden = m === 0;
        jumps.forEach((a) => { const g = box.querySelector('#cepa-l-' + a.getAttribute('data-cepa-jump').toLowerCase()); a.classList.toggle('is-off', !g || g.hidden); });
        // final fix 10-10: the count is search feedback only (at rest the hero carries the ONE brand number)
        if (count) count.textContent = !term ? '' : (n === 1 && t('one') ? t('one') : t('count').replace('[count]', n));
        if (none) {
          none.hidden = n + m > 0;
          if (noneText) noneText.textContent = t('none').replace('[q]', q.value.trim());
          if (searchBtn) searchBtn.setAttribute('href', '/search?q=' + encodeURIComponent(q.value.trim()));
        }
      };
      q.addEventListener('input', run);
      q.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const first = tiles.find((li) => !li.hidden);
        if (first && norm(q.value)) first.querySelector('a').focus();
      });
    });
  }

  /* ---------- SilverChef: the weekly guide (price x rate x 12 / 52, the live product page's own sum) ---------- */
  function calc(root) {
    root.querySelectorAll('[data-cepa-calc]').forEach((box) => {
      if (!once(box, 'Calc')) return;
      const input = box.querySelector('[data-cepa-calc-input]');
      const price = box.querySelector('[data-cepa-calc-price]');
      const week = box.querySelector('[data-cepa-calc-week]');
      const rate = parseFloat(box.getAttribute('data-rate')) || 0.055;
      if (!input) return;
      const fmt0 = (n) => '$' + Math.round(n).toLocaleString('en-AU');
      const fmt2 = (n) => '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const paint = () => {
        const v = parseFloat(input.value) || 0;
        const pct = (v - parseFloat(input.min)) / ((parseFloat(input.max) - parseFloat(input.min)) || 1) * 100;
        input.style.setProperty('--p', pct + '%');
        if (price) price.textContent = fmt0(v);
        if (week) week.textContent = fmt2(v * rate * 12 / 52);
      };
      input.addEventListener('input', paint);
      paint();
    });
  }

  /* ---------- Warranty: the page's own text in parts; phones start with the first part open ---------- */
  function details(root) {
    root.querySelectorAll('[data-cepa-parts]').forEach((box) => {
      if (!once(box, 'Parts')) return;
      if (W.matchMedia('(max-width: 749px)').matches) box.querySelectorAll('details.cepa-part').forEach((d, i) => { if (i > 0) d.open = false; });
      D.querySelectorAll('.cepa-toc a[href^="#cepa-w-"]').forEach((a) => a.addEventListener('click', () => { const d = D.getElementById(a.getAttribute('href').slice(1)); if (d && d.tagName === 'DETAILS') d.open = true; }));
    });
  }

  const parts = [draw, finder, contact, quote, wall, calc, details];
  function init(root) { parts.forEach((p) => { try { p(root || D); } catch (e) { if (W.console) console.warn('ce-pages-a', e); } }); }
  W.CEPA = { init, parts, esc, norm, once, reduce };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
})();
